import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import SyncChip from "@/components/SyncChip";
import OfflineIndicator from "@/components/OfflineIndicator";
import { queueSubmission } from "@/lib/outbox";
import { cachePut, cacheGet } from "@/lib/refcache";
import { CaretLeft, CaretRight, MagicWand, PaperPlaneTilt, Clock } from "@phosphor-icons/react";

const inp = "w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-[16px] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)] disabled:bg-surface-inset disabled:text-ink-disabled";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function mondayOf(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hhmm = (s) => { try { return new Date(s).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return ""; } };
function calcHours(e) {
  if (!e.start || !e.finish) return Number(e.hours) || 0;
  const [a, b] = [e.start, e.finish].map((t) => { const [h, m] = t.split(":"); return +h + (+m || 0) / 60; });
  let d = b - a; if (d < 0) d += 24;
  return Math.max(0, +(d - (Number(e.break_minutes) || 0) / 60).toFixed(2));
}
const blankWeek = (ws) => [...Array(7)].map((_, i) => {
  const d = new Date(ws + "T00:00:00"); d.setDate(d.getDate() + i);
  return { date: iso(d), project_name: "", start: "", finish: "", break_minutes: 0, hours: 0, notes: "" };
});

export default function FieldTimesheet() {
  const [saved] = useState(() => { try { return JSON.parse(localStorage.getItem("eq_field_worker") || "null"); } catch { return null; } });
  const [weekStart, setWeekStart] = useState(() => iso(mondayOf(new Date())));
  const [entries, setEntries] = useState(blankWeek(iso(mondayOf(new Date()))));
  const [status, setStatus] = useState("draft");
  const [attendance, setAttendance] = useState([]);
  const [workerName, setWorkerName] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);

  const load = useCallback(async () => {
    if (!saved?.id || !saved?.token) return;
    setMsg(""); setError(""); setStale(false);
    try {
      const { data } = await api.get(`/public/worker-timesheet/${saved.id}`, { params: { token: saved.token, week_start: weekStart } });
      setWorkerName(data.worker_name);
      setStatus(data.timesheet?.status || "draft");
      setEntries(blankWeek(weekStart).map((b) => (data.timesheet?.entries || []).find((e) => e.date === b.date) || b));
      setAttendance(data.attendance || []);
      cachePut(`fts:${saved.id}:${weekStart}`, data, null).catch(() => {});
    } catch (e) {
      if (!e.response) {
        const hit = await cacheGet(`fts:${saved.id}:${weekStart}`).catch(() => null);
        if (hit) {
          setWorkerName(hit.data.worker_name);
          setStatus(hit.data.timesheet?.status || "draft");
          setEntries(blankWeek(weekStart).map((b) => (hit.data.timesheet?.entries || []).find((x) => x.date === b.date) || b));
          setAttendance(hit.data.attendance || []);
          setStale(true);
        } else setError("No connection and no saved copy for this week yet.");
      } else setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [saved, weekStart]);
  useEffect(() => { load(); }, [load]);

  const locked = status !== "draft";
  const setE = (i, k, v) => setEntries((rows) => rows.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const shift = (days) => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + days); setWeekStart(iso(d)); };

  const prefill = () => setEntries((rows) => rows.map((r) => {
    const recs = attendance.filter((x) => x.date === r.date && x.sign_in_at);
    if (!recs.length) return r;
    const first = recs.reduce((a, b) => (a.sign_in_at < b.sign_in_at ? a : b));
    const outs = recs.filter((x) => x.sign_out_at);
    const last = outs.length ? outs.reduce((a, b) => (a.sign_out_at > b.sign_out_at ? a : b)) : null;
    const brk = recs.reduce((s, x) => s + (Number(x.break_minutes) || 0), 0);
    return { ...r, project_name: first.project_name || r.project_name, start: hhmm(first.sign_in_at),
             finish: last ? hhmm(last.sign_out_at) : r.finish,
             break_minutes: brk || r.break_minutes || 0 };
  }));

  const save = async (submit) => {
    setBusy(true); setMsg(""); setError("");
    const body = {
      worker_id: saved.id, token: saved.token, week_start: weekStart, submit,
      client_ref: `fts-${saved.id}-${weekStart}`,
      entries: entries.map((e) => ({ ...e, break_minutes: Number(e.break_minutes) || 0, hours: calcHours(e) })),
    };
    try {
      await api.post("/public/worker-timesheet", body);
      if (submit) setStatus("submitted");
      setMsg(submit ? "Timesheet submitted — the office will review and approve it." : "Saved.");
    } catch (e) {
      if (!e.response) {
        await queueSubmission("timesheet", "/public/worker-timesheet", body).catch(() => {});
        if (submit) setStatus("submitted");
        setMsg("No signal — saved on this device and will send automatically when you're back in coverage.");
      } else setError(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };

  if (!saved?.id || !saved?.token) {
    return (
      <div className="min-h-screen bg-equipr-black flex flex-col items-center justify-center px-6 text-center" data-testid="field-timesheet-nodevice">
        <BrandLogo variant="dark" showProduct={false} />
        <p className="text-white font-bold mt-6">This device isn't linked to a worker yet.</p>
        <p className="text-gray-400 text-sm mt-2">Sign in at a site QR code first — after that your timesheet will appear here.</p>
        <Link to="/field" className="mt-6 bg-equipr-yellow text-equipr-black font-bold px-5 py-2.5 text-sm">Back to Field Hub</Link>
      </div>
    );
  }

  const total = entries.reduce((s, e) => s + calcHours(e), 0).toFixed(2);
  return (
    <div className="min-h-screen bg-surface-page flex flex-col" data-testid="field-timesheet">
      <OfflineIndicator />
      <div className="bg-equipr-black px-4 py-3 flex items-center justify-between">
        <BrandLogo variant="dark" showProduct={false} />
        <span className="flex items-center gap-2"><SyncChip /><Link to="/field" className="text-xs font-bold uppercase tracking-widest text-equipr-yellow">Field Hub</Link></span>
      </div>
      <div className="flex-1 p-4 max-w-2xl w-full mx-auto space-y-3 pb-10">
        <h1 className="text-xl font-extrabold text-equipr-black inline-flex items-center gap-2"><Clock size={20} weight="bold" className="text-accent-ink" /> My Timesheet</h1>
        {workerName && <p className="text-sm text-gray-600 -mt-2">{workerName}{stale && <span className="ml-2 text-amber-600 font-semibold">(offline copy)</span>}</p>}

        <div className="flex items-center justify-between bg-white border border-gray-200 px-3 py-2">
          <button onClick={() => shift(-7)} data-testid="fts-week-prev" className="h-9 w-9 border border-gray-300 inline-flex items-center justify-center"><CaretLeft size={14} /></button>
          <span className="text-sm font-bold" data-testid="fts-week-label">Week of {weekStart}</span>
          <button onClick={() => shift(7)} data-testid="fts-week-next" className="h-9 w-9 border border-gray-300 inline-flex items-center justify-center"><CaretRight size={14} /></button>
        </div>

        {locked && (
          <div className={`px-3 py-2.5 text-sm font-semibold ${status === "approved" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`} data-testid="fts-locked-banner">
            {status === "approved" ? "✔ Approved by the office — locked." : "Submitted — waiting for office approval. Contact the office to make changes."}
          </div>
        )}
        {msg && <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2" data-testid="fts-msg">{msg}</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="fts-error">{error}</div>}

        {!locked && (
          <button onClick={prefill} data-testid="fts-prefill" className="h-9 px-3 border border-equipr-black text-xs font-bold bg-white hover:bg-gray-50 inline-flex items-center gap-1.5">
            <MagicWand size={13} weight="bold" /> Fill from my sign-ins
          </button>
        )}

        {entries.map((e, i) => (
          <div key={e.date} className="bg-white border border-gray-200 p-3 space-y-2" data-testid={`fts-day-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm">{DAYS[i]} <span className="text-gray-400 font-normal">{e.date.slice(8)}/{e.date.slice(5, 7)}</span></span>
              <span className="text-sm font-extrabold" data-testid={`fts-hours-${i}`}>{calcHours(e) ? `${calcHours(e)} h` : ""}</span>
            </div>
            <input className={inp} placeholder="Project / site" value={e.project_name || ""} disabled={locked} onChange={(ev) => setE(i, "project_name", ev.target.value)} data-testid={`fts-project-${i}`} />
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] font-semibold text-gray-500">Start</label>
                <input type="time" className={inp} value={e.start || ""} disabled={locked} onChange={(ev) => setE(i, "start", ev.target.value)} data-testid={`fts-start-${i}`} /></div>
              <div><label className="text-[10px] font-semibold text-gray-500">Finish</label>
                <input type="time" className={inp} value={e.finish || ""} disabled={locked} onChange={(ev) => setE(i, "finish", ev.target.value)} data-testid={`fts-finish-${i}`} /></div>
              <div><label className="text-[10px] font-semibold text-gray-500">Break (min)</label>
                <input type="number" min="0" step="5" inputMode="numeric" className={inp} value={e.break_minutes} disabled={locked} onChange={(ev) => setE(i, "break_minutes", ev.target.value)} data-testid={`fts-break-${i}`} /></div>
            </div>
          </div>
        ))}

        <div className="bg-equipr-black text-white px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-bold">Week total</span>
          <span className="text-xl font-extrabold text-equipr-yellow" data-testid="fts-total">{total} h</span>
        </div>

        {!locked && (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => save(false)} disabled={busy} data-testid="fts-save" className="h-12 border-2 border-equipr-black bg-white text-equipr-black font-bold text-sm disabled:opacity-50">Save draft</button>
            <button onClick={() => save(true)} disabled={busy} data-testid="fts-submit" className="h-12 bg-equipr-yellow text-equipr-black font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              <PaperPlaneTilt size={15} weight="bold" /> Submit week
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
